import { FC, useEffect, useState, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import {
  FlowError,
  FlowRadioGroup,
  Header,
  Layout,
  NextButton,
  STEP_TITLE_ID,
} from '@eneco/flows/components';
import { requiredChoiceMessage } from '@eneco/flows/helpers';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { NotificationBox, RadioTile, TextLink, VisuallyHidden } from '@eneco/sparky';
import {
  Electricity,
  ElectricityAndGas,
  ElectricityAndHeating,
  ElectricityGasHeating,
  Gas,
  Heating,
} from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { DialogField, EnergyChoiceStepRendering } from '@sitecore/types/EnergyChoiceStep';
import { useMachine as useMachineUpdate } from '@xstate/react';
import { useForm } from 'react-hook-form';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';
import { energyChoiceTypeMachine } from '../../machines/energyChoice/energyChoice.machine';
import { EnergyType } from '../../types';

const ENERGY_CHOICE_LABEL_ID = 'energy-choice-label';

interface FormFields {
  energyType?: EnergyType;
}

const generateInputElement = (value: EnergyType, label: string): JSX.Element => {
  let icon: JSX.Element;

  switch (value) {
    case EnergyType.Gas:
      icon = <Gas size="small" />;
      break;

    case EnergyType.ElectricityAndGas:
      icon = <ElectricityAndGas size="small" />;
      break;

    case EnergyType.ElectricityAndGasAndWarmth:
    case EnergyType.ElectricityAndGasAndTapWater:
    case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
    case EnergyType.ElectricityAndGasAndWarmthAndTapWaterAndCooling:
      icon = <ElectricityGasHeating size="small" />;
      break;

    case EnergyType.ElectricityAndWarmth:
    case EnergyType.ElectricityAndWarmthAndCooling:
    case EnergyType.ElectricityAndTapWater:
    case EnergyType.ElectricityAndWarmthAndTapWater:
    case EnergyType.ElectricityAndWarmthAndTapWaterAndCooling:
    case EnergyType.ElectricityAndGeothermalHeating:
      icon = <ElectricityAndHeating size="small" />;
      break;

    case EnergyType.Warmth:
    case EnergyType.TapWater:
    case EnergyType.WarmthAndCooling:
    case EnergyType.WarmthAndTapWater:
    case EnergyType.WarmthAndTapWaterAndCooling:
    case EnergyType.GeothermalHeating:
      icon = <Heating size="small" />;
      break;

    default:
      icon = <Electricity size="small" />;
  }

  return (
    <RadioTile key={value} value={value} icon={icon}>
      {label}
    </RadioTile>
  );
};

export const EnergyChoiceStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: flowContext,
    context: { energyType },
  } = flowState;

  const [energyChoiceState, sendToEnergyChoiceMachine] = useMachineUpdate(energyChoiceTypeMachine, {
    input: { businessUnit: businessUnit, label: label },
  });

  const {
    context: { availableProducts = [] },
  } = energyChoiceState;

  const { EnergyChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    EnergyChoiceStep: EnergyChoiceStepRendering;
  }>();

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      // For this step we won't be using the "defaultValue" straight away since the earlier
      // selected "energyType" might not no longer be available due to address change
    },
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotSupported, setIsNotSupported] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);

  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const watchEnergyType = watch('energyType');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      type: 'FETCH_ENERGY_TYPES',
      values: { ...flowContext },
    };
    sendToEnergyChoiceMachine(payload);
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      updateContextAndGoToNextStep({
        ...getValues(),
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setShouldShowOfferAvailabilityNotification(
      !!(
        watchEnergyType &&
        watchEnergyType !== EnergyType.ElectricityAndGas &&
        watchEnergyType !== EnergyType.Warmth &&
        watchEnergyType !== EnergyType.WarmthAndTapWater &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content
      ),
    );
  }, [watchEnergyType]);

  useEffect(() => {
    if (!energyChoiceState.matches('IDLE')) {
      setIsLoading(energyChoiceState.matches('FETCH'));
      setIsFailed(!energyChoiceState.matches('FETCH') && !energyChoiceState.matches('SUCCESS'));
      setIsSuccessful(energyChoiceState.matches('SUCCESS'));

      setIsNotFound(energyChoiceState.matches('ERROR_NOT_FOUND'));
      setIsNotSupported(energyChoiceState.matches('ERROR_NOT_SUPPORTED'));

      if (energyChoiceState.matches('SUCCESS')) {
        setValue(
          'energyType',
          availableProducts?.find((product: EnergyType) => product === energyType),
        );
      }
    }
  }, [energyChoiceState]);

  // If "availableProducts" list includes "EnergyType.Warmth" then
  // we should render "EnergyType.Warmth" specific text otherwise default text
  const text: string | undefined = availableProducts.some((product: EnergyType) => product.includes(EnergyType.Warmth))
    ? textualData?.energyChoice?.warmthAvailableOptionText?.value
    : textualData?.content?.text?.value;

  // If "availableProducts" list includes "EnergyType.Warmth" then
  // we should render "EnergyType.Warmth" specific dialog
  const dialog: DialogField | undefined = availableProducts.some((product: EnergyType) =>
    product.includes(EnergyType.Warmth),
  )
    ? textualData?.content?.dialog
    : undefined;

  const getTextLabel = (product: EnergyType): string => {
    let textLabel = '';

    switch (product) {
      case EnergyType.ElectricityAndGasAndTapWater:
      case EnergyType.ElectricityAndGasAndWarmthAndTapWater:
      case EnergyType.ElectricityAndGasAndWarmthAndTapWaterAndCooling:
        textLabel =
          textualData?.energyChoice?.energyChoiceRadio?.value?.options?.find(
            option => option.name === EnergyType.ElectricityAndGasAndWarmth,
          )?.label ?? '';
        break;

      case EnergyType.ElectricityAndTapWater:
      case EnergyType.ElectricityAndWarmthAndTapWater:
      case EnergyType.ElectricityAndWarmthAndTapWaterAndCooling:
        textLabel =
          textualData?.energyChoice?.energyChoiceRadio?.value?.options?.find(
            option => option.name === EnergyType.ElectricityAndWarmth,
          )?.label ?? '';
        break;

      case EnergyType.TapWater:
      case EnergyType.WarmthAndTapWater:
      case EnergyType.WarmthAndTapWaterAndCooling:
        textLabel =
          textualData?.energyChoice?.energyChoiceRadio?.value?.options?.find(
            option => option.name === EnergyType.Warmth,
          )?.label ?? '';
        break;

      default:
        textLabel =
          textualData?.energyChoice?.energyChoiceRadio?.value?.options?.find(
            // since cooling is not presented as a loose product we strip it out of the "product" name
            option => option.name === product.replace('AndCooling', ''),
          )?.label ?? '';
    }

    return textLabel;
  };

  const energyChoiceLabel = textualData?.energyChoice?.energyChoiceRadio?.value?.label?.trim();

  const getErrorNotification = () => {
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;
    if (isNotSupported) return textualData?.genericError?.errorNotSupportedNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="A" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <Header>
            <Header.Title id={STEP_TITLE_ID}>{textualData?.content?.title?.value}</Header.Title>

            {text && (
              <Header.Text>
                <RichText html={text} />{' '}
                {dialog && (
                  <TrackedDialog title={dialog.value?.title} trigger={<TextLink>{dialog.value?.triggerText}</TextLink>}>
                    <RichText html={dialog?.value?.content} />
                  </TrackedDialog>
                )}
              </Header.Text>
            )}
          </Header>

          {textualData?.content?.notification?.value?.content && (
            <NotificationBox
              isAlert={false}
              variant={textualData?.content?.notification?.value?.variant}
              title={textualData?.content?.notification?.value?.title}
              text={<RichText html={textualData?.content?.notification?.value?.content} />}
            />
          )}

          {energyChoiceLabel ? (
            <VisuallyHidden id={ENERGY_CHOICE_LABEL_ID}>{energyChoiceLabel}</VisuallyHidden>
          ) : null}

          <FlowRadioGroup
            control={control}
            name="energyType"
            rules={{
              required: requiredChoiceMessage(textualData?.energyChoice?.energyChoiceRadio?.value?.requiredMessage),
            }}
            aria-labelledby={energyChoiceLabel ? ENERGY_CHOICE_LABEL_ID : STEP_TITLE_ID}
            hint={textualData?.energyChoice?.energyChoiceRadio?.value?.hint || undefined}
            direction="column"
            error={errors['energyType']?.message}>
            {availableProducts.map((product: EnergyType) => {
              const textLabel = getTextLabel(product);
              return generateInputElement(product, textLabel);
            })}
          </FlowRadioGroup>

          {shouldShowOfferAvailabilityNotification && (
            <TrackedNotificationBox
              isAlert={false}
              label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
              title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
              text={
                <RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />
              }
              variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
            />
          )}

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
        </>
      )}

      {isFailed && (
        <FlowError
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotFound && !isNotSupported ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
